import { useMemo } from 'react'
import CruiseBookingConditionsInfo from 'components/Cruises/CruiseInclusion/CruiseBookingConditionsInfo'
import CruiseOfferInclusionItem from 'components/Cruises/CruiseInclusion/CruiseOfferInclusionItem'
import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getDistinctGrowthInclusions } from 'lib/offer/growthOfferTile'
import GrowthInclusionList from './GrowthInclusionList'

interface Props {
  evergreenInclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
}

/**
 * Compact counterpart of components/Cruises/CruiseInclusion/CruiseInclusionListHomeTile.tsx.
 * Reuse its inclusion builder, booking-condition classification and item renderer,
 * but show single-line benefits in the Growth preview rather than an expanding list.
 * CruiseOfferCard owns the member labels, which the Growth layout places above pricing.
 */
function GrowthCruiseInclusions({ standardInclusions, evergreenInclusions = EmptyArray, tileInclusions }: Props) {
  const promotional = useMemo(() => buildAllStandardInclusions(standardInclusions, tileInclusions, true), [standardInclusions, tileInclusions])
  const conditions = useMemo(() => promotional.filter(inclusion => inclusion.displayType === 'BOOKING_CONDITION'), [promotional])
  const benefits = useMemo(() => getDistinctGrowthInclusions(
    [...promotional, ...evergreenInclusions].filter(inclusion => inclusion.displayType !== 'BOOKING_CONDITION'),
  ), [promotional, evergreenInclusions])
  // Conditions are disclosures, not benefits: exclude them from +N more, but keep
  // their tooltip on a visible row so truncating the preview cannot hide it.
  const conditionInclusionId = benefits.slice(0, 3).findLast(inclusion => promotional.some(item => item.id === inclusion.id))?.id ?? benefits[Math.min(benefits.length, 3) - 1]?.id

  return <GrowthInclusionList>
    {benefits.map(inclusion => <CruiseOfferInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      size="small"
      lineClamp={1}
      showMinStayDescription
      endIcon={conditions.length > 0 && inclusion.id === conditionInclusionId ? <CruiseBookingConditionsInfo conditions={conditions} /> : undefined}
    />)}
  </GrowthInclusionList>
}

export default GrowthCruiseInclusions
